import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { BillingGateService } from '../../../core/billing-gate';
import { TenantService } from '../../../core/tenant/tenant.service';
import type { InvoiceSummaryModel, SubscriptionModel, UsageModel } from '../models/billing.models';
import { BillingApiService } from '../services/billing-api.service';

const POLL_INTERVAL_MS = 5_000;
// Pix DueDate é now + 2h. Manter o polling pelo período inteiro pra detectar
// `Active` pelo webhook mesmo se o usuário pagar no fim do prazo.
const POLL_MAX_DURATION_MS = 2 * 60 * 60_000;

@Injectable({ providedIn: 'root' })
export class BillingStore {
  private readonly tenant = inject(TenantService);
  private readonly api = inject(BillingApiService);
  private readonly billingGate = inject(BillingGateService);
  private readonly loadedCompanyIdSignal = signal<string | null>(null);

  private readonly subscriptionSignal = signal<SubscriptionModel | null>(null);
  private readonly usageSignal = signal<UsageModel | null>(null);
  private readonly invoicesSignal = signal<InvoiceSummaryModel[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);

  private pollingTimer: ReturnType<typeof setInterval> | null = null;
  private pollingStartedAt: number | null = null;

  readonly subscription = this.subscriptionSignal.asReadonly();
  readonly usage = this.usageSignal.asReadonly();
  readonly invoices = this.invoicesSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  readonly hasSubscription = computed(() => this.subscription() !== null);
  readonly status = computed(() => this.subscription()?.status ?? null);

  constructor() {
    effect(() => {
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.loadedCompanyIdSignal.set(null);
        this.reset();
        this.stopPolling();
        return;
      }
      if (this.loadedCompanyIdSignal() !== companyId) {
        this.loadedCompanyIdSignal.set(companyId);
        this.reset();
        void this.refresh();
      }
    });

    effect(() => {
      const status = this.status();
      if (status === 'Pending') {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    });

    // Libera o billing gate assim que a assinatura volta a estar ativa.
    // `Active` é o único gatilho: Canceled mantém acesso até a data-limite,
    // Pending mantém acesso durante o grace period (definido pelo backend),
    // e se voltar a cair em 402 o interceptor re-ativa o gate.
    effect(() => {
      if (this.status() === 'Active' && this.billingGate.blocked()) {
        this.billingGate.clear();
      }
    });
  }

  async refresh(): Promise<void> {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const [subscription, usage] = await Promise.all([
        this.api.getCurrentSubscription(),
        this.api.getUsage().catch(() => null),
      ]);

      if (!this.isSameCompany(requestCompanyId)) return;
      this.subscriptionSignal.set(subscription);
      this.usageSignal.set(usage);

      if (subscription?.status === 'Active' || subscription?.status === 'Canceled') {
        void this.loadInvoices();
      }
    } catch (error) {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.errorSignal.set(this.resolveErrorMessage(error));
    } finally {
      if (this.isSameCompany(requestCompanyId)) {
        this.loadingSignal.set(false);
      }
    }
  }

  async loadInvoices(): Promise<void> {
    try {
      const invoices = await this.api.getInvoices();
      this.invoicesSignal.set(invoices);
    } catch {
      this.invoicesSignal.set([]);
    }
  }

  reset(): void {
    this.subscriptionSignal.set(null);
    this.usageSignal.set(null);
    this.invoicesSignal.set([]);
    this.loadingSignal.set(false);
    this.errorSignal.set(null);
  }

  private startPolling(): void {
    if (this.pollingTimer) return;
    this.pollingStartedAt = Date.now();
    this.pollingTimer = setInterval(() => {
      const elapsed = Date.now() - (this.pollingStartedAt ?? 0);
      if (elapsed > POLL_MAX_DURATION_MS) {
        this.stopPolling();
        return;
      }
      void this.refresh();
    }, POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.pollingStartedAt = null;
  }

  private isSameCompany(requestCompanyId: string): boolean {
    return this.tenant.companyId() === requestCompanyId;
  }

  private resolveErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
    return 'billing.errors.loadFailed';
  }
}
